import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  TextField,
  Dialog
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import RolePermissionPage from './RolePermissionPage';
import UserForm from './PropertyPanel/UserForm';
import { LoginLogTable } from './components/LoginLogTable';
import { fetchPropertyUsers } from './api/settingsApi';

const sectionDefinitions = [
  { key: 'users', label: 'User Management' },
  { key: 'roles', label: 'Role & Permission' },
  { key: 'loginlogs', label: 'Login Logs' }
];

const getCurrentUser = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const SettingsPage = () => {
  const [propertyId, setPropertyId] = useState(null);
  const [section, setSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userError, setUserError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const currentUser = useMemo(getCurrentUser, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const pid = storedUser?.propertyId?.id || storedUser?.properties?.[0]?.propertyId?.id;
    setPropertyId(pid);
  }, []);

  const loadUsers = useCallback(async () => {
    if (!propertyId || propertyId.trim() === '') {
      setUsers([]);
      setUserError('Property ID is missing');
      return;
    }

    setLoadingUsers(true);
    setUserError(null);
    try {
      const fetched = await fetchPropertyUsers(propertyId);
      setUsers(fetched);
    } catch (err) {
      console.error('[SettingsPage] Failed to load property users', err);
      setUserError(err.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [propertyId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSaveUser = async (userData) => {
    const url = userData._id ? `${process.env.REACT_APP_API_URL || 'http://localhost:7071'}/api/property/users/${userData._id}` : `${process.env.REACT_APP_API_URL || 'http://localhost:7071'}/api/property/users`;
    const method = userData._id ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error || 'Failed to save user');
    }

    loadUsers();
  };

  const filteredRows = useMemo(() => {
    const normalized = searchTerm.toLowerCase().trim();
    return users
      .map((user) => ({
        id: user._id,
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.properties?.[0]?.role || user.role || 'receptionist',
        original: user
      }))
      .filter((row) => {
        if (!normalized) return true;
        return (
          row.username.toLowerCase().includes(normalized) ||
          row.email.toLowerCase().includes(normalized) ||
          row.phone.toLowerCase().includes(normalized) ||
          row.role.toLowerCase().includes(normalized)
        );
      });
  }, [users, searchTerm]);

  const columns = [
    { field: 'username', headerName: 'Username', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.2 },
    { field: 'phone', headerName: 'Phone', flex: 0.8 },
    { field: 'role', headerName: 'Role', flex: 0.8 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          startIcon={<EditIcon />}
          size="small"
          onClick={() => {
            setEditingUser(params.row.original);
            setOpenForm(true);
          }}
        >
          Edit
        </Button>
      )
    }
  ];

  const renderUsersSection = () => (
    <Paper sx={{ p: 3, boxShadow: 2 }}>
      <Box display="flex" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h6" fontWeight={600}>
          User Management
        </Typography>
        <Button variant="contained" onClick={() => setOpenForm(true)}>
          + New User
        </Button>
      </Box>
      <Box mb={2}>
        <TextField
          fullWidth
          label="Search Users"
          placeholder="Username, email, phone or role"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </Box>
      <Box sx={{ height: 520 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loadingUsers}
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          components={{
            NoRowsOverlay: () => (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <Typography color="textSecondary">
                  {userError ? userError : loadingUsers ? 'Loading...' : 'No users found'}
                </Typography>
              </Box>
            )
          }}
        />
      </Box>
    </Paper>
  );

  const renderRolesSection = () => (
    <Paper sx={{ p: 3, boxShadow: 2 }}>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Role &amp; Permission
      </Typography>
      <RolePermissionPage />
    </Paper>
  );

  const renderLogsSection = () => (
    <Paper sx={{ p: 3, boxShadow: 2 }}>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Login Logs
      </Typography>
      <LoginLogTable />
    </Paper>
  );

  const renderSection = () => {
    switch (section) {
      case 'roles':
        return renderRolesSection();
      case 'loginlogs':
        return renderLogsSection();
      default:
        return renderUsersSection();
    }
  };

  return (
    <Box minHeight="100vh" bgcolor="#f4f7fa">
      <Box
        maxWidth={1200}
        mx="auto"
        py={4}
        px={2}
        display="flex"
        gap={3}
        flexDirection={{ xs: 'column', md: 'row' }}
      >
        <Paper sx={{ width: 220, p: 2, boxShadow: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            Settings
          </Typography>
          <Divider sx={{ my: 1 }} />
          <List disablePadding>
            {sectionDefinitions.map((item) => (
              <ListItemButton
                key={item.key}
                selected={section === item.key}
                onClick={() => setSection(item.key)}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Box flex={1}>{renderSection()}</Box>
      </Box>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="sm">
        <UserForm
          initialData={editingUser || {}}
          currentUserId={currentUser?._id}
          currentUserRole={currentUser?.properties?.[0]?.role}
          onSave={async (payload) => {
            await handleSaveUser(payload);
            setOpenForm(false);
            setEditingUser(null);
          }}
          onCancel={() => {
            setOpenForm(false);
            setEditingUser(null);
          }}
        />
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
